// core/srs — FSRS spaced-repetition scheduler (pure). Replaces SM-2 as the one family algorithm
// (sm2.js stays exported + tested as reference; the store routes all reviews through here).
//
// In-house port of FSRS-4.5 (Free Spaced Repetition Scheduler) — the published equations + default
// parameters from open-spaced-repetition/fsrs4anki (MIT). Deterministic: whole-day intervals, NO
// interval fuzz in v1. Desired retention 0.9. State is immutable — review() returns a new frozen
// object. No new dependency; ~1 file mirroring sm2.js's pure pattern.
//
// COMPAT: FSRS-era state carries a synthesized `ease` field DERIVED from `difficulty` via the
// inverse bijection below — it exists ONLY so the ease-keyed weak-topic system (weakSignals,
// tagSummary, quizQueue, flagWeakTopics/WEAK_EASE) keeps working unchanged. It is NOT scheduler
// state; FSRS schedules on stability + difficulty. Legacy SM-2 records keep their real ease
// (harmless), and recordReview lazily converts them on next review.

/** The four review buttons, mapped to FSRS ratings (1..4). Again is a lapse (forget). */
export const RATINGS = Object.freeze({ again: 1, hard: 2, good: 3, easy: 4 });

// FSRS-4.5 default parameters (17 weights).
const W = Object.freeze([
  0.4872, 1.4003, 3.7145, 13.8206, 5.1618, 1.2298, 0.8975, 0.031, 1.6474,
  0.1367, 1.0461, 2.1072, 0.0793, 0.3246, 1.587, 0.2272, 2.8755,
]);

const DECAY = -0.5;
const FACTOR = Math.pow(0.9, 1 / DECAY) - 1; // = 19/81; so interval == stability at retention 0.9
const REQUEST_RETENTION = 0.9;
const MIN_STABILITY = 0.1;
const DAY_MS = 86_400_000;

const nowIso = () => new Date().toISOString();
const round2 = (n) => Math.round(n * 100) / 100;
const round4 = (n) => Math.round(n * 10_000) / 10_000;
const clampD = (d) => Math.min(10, Math.max(1, d));
const addDays = (iso, days) => new Date(Date.parse(iso) + days * DAY_MS).toISOString();

// ── The ease ↔ difficulty bijection (ONE pair, so the two directions can never drift) ──────────
// ease 1.3..2.8  ↔  difficulty 10..1 (linear). difficultyFromEase powers the lazy SM-2 migration;
// easeFromDifficulty powers the compat `ease` synthesis. Round-trips exactly at the endpoints.
const EASE_LO = 1.3, EASE_HI = 2.8, DIFF_LO = 1, DIFF_HI = 10;

/** Map a legacy SM-2 ease (1.3..2.8) to an FSRS difficulty (10..1), clamped. */
export function difficultyFromEase(ease) {
  const e = Math.min(EASE_HI, Math.max(EASE_LO, ease));
  return clampD(DIFF_HI - ((e - EASE_LO) / (EASE_HI - EASE_LO)) * (DIFF_HI - DIFF_LO));
}

/** Map an FSRS difficulty (1..10) back to an ease-like number (2.8..1.3) for the weak-topic system. */
export function easeFromDifficulty(difficulty) {
  const d = clampD(difficulty);
  return EASE_HI - ((d - DIFF_LO) / (DIFF_HI - DIFF_LO)) * (EASE_HI - EASE_LO);
}

// ── FSRS core equations ────────────────────────────────────────────────────────────────────────

/** Whole-day interval for a given stability at the desired retention (min 1 day, no fuzz). */
function intervalFrom(stability) {
  const raw = (stability / FACTOR) * (Math.pow(REQUEST_RETENTION, 1 / DECAY) - 1);
  return Math.max(1, Math.round(raw));
}

/** Retrievability after `elapsedDays` at a given stability (the power-law forgetting curve).
 * Exported for the forecast module (FC·1) — the projection MUST be this exact curve, never a copy. */
export function retrievability(elapsedDays, stability) {
  return Math.pow(1 + FACTOR * (elapsedDays / stability), DECAY);
}

/**
 * OP·2 — build a scheduler bound to a specific FSRS-4.5 weight vector (the per-app
 * personalization seam; open-spaced-repetition/fsrs-optimizer produces the vector externally).
 * The module-level `initState`/`review`/`preview` exports stay bound to the DEFAULTS below —
 * byte-identical behavior when no per-app params exist. Throws on a malformed vector.
 */
export function createScheduler(weights = W) {
  if (!Array.isArray(weights) || weights.length !== W.length ||
      !weights.every((n) => typeof n === "number" && Number.isFinite(n))) {
    throw new Error(`FSRS weights must be an array of ${W.length} finite numbers`);
  }
  const w = Object.freeze([...weights]);

  const initStability = (rating) => Math.max(MIN_STABILITY, w[rating - 1]); // w0..w3
  const initDifficulty = (rating) => clampD(w[4] - w[5] * (rating - 3));

  // Difficulty after a review, with mean reversion toward the "good" initial difficulty (w[4]).
  function nextDifficulty(d, rating) {
    const next = d - w[6] * (rating - 3);
    return clampD(w[7] * w[4] + (1 - w[7]) * next);
  }

  // Stability after a successful review (hard/good/easy) — hard penalty / easy bonus.
  function stabilityAfterRecall(d, s, r, rating) {
    const hardPenalty = rating === RATINGS.hard ? w[15] : 1;
    const easyBonus = rating === RATINGS.easy ? w[16] : 1;
    const growth =
      Math.exp(w[8]) * (11 - d) * Math.pow(s, -w[9]) *
      (Math.exp(w[10] * (1 - r)) - 1) * hardPenalty * easyBonus;
    return s * (1 + growth);
  }

  // Stability after a lapse (again) — never increases (min with the prior stability).
  function stabilityAfterForget(d, s, r) {
    const forget = w[11] * Math.pow(d, -w[12]) * (Math.pow(s + 1, w[13]) - 1) * Math.exp(w[14] * (1 - r));
    return Math.max(MIN_STABILITY, Math.min(forget, s));
  }

  function initState(grade, now = nowIso()) {
    const rating = RATINGS[grade];
    if (rating === undefined) throw new Error(`unknown grade: ${grade}`);
    const stability = initStability(rating);
    const difficulty = initDifficulty(rating);
    const lapse = rating === RATINGS.again;
    return freezeState(stability, difficulty, intervalFrom(stability), addDays(now, intervalFrom(stability)), now, lapse ? 0 : 1, lapse ? 1 : 0);
  }

  function review(state, grade, now = nowIso()) {
    const rating = RATINGS[grade];
    if (rating === undefined) throw new Error(`unknown grade: ${grade}`);
    if (state == null) return initState(grade, now);

    let { stability, difficulty, repetitions = 0, lapses = 0, lastReviewed } = state;
    if (stability === undefined) {
      // lazy SM-2 → FSRS conversion
      stability = Math.max(MIN_STABILITY, state.intervalDays || MIN_STABILITY);
      difficulty = difficultyFromEase(typeof state.ease === "number" ? state.ease : 2.5);
    }

    const elapsedDays = typeof lastReviewed === "string"
      ? Math.max(0, (Date.parse(now) - Date.parse(lastReviewed)) / DAY_MS)
      : 0;
    const r = retrievability(elapsedDays, stability);
    const nextD = nextDifficulty(difficulty, rating);
    const nextS = rating === RATINGS.again
      ? stabilityAfterForget(difficulty, stability, r)
      : stabilityAfterRecall(difficulty, stability, r, rating);
    const interval = intervalFrom(nextS);
    const lapse = rating === RATINGS.again;
    return freezeState(nextS, nextD, interval, addDays(now, interval), now, lapse ? repetitions : repetitions + 1, lapse ? lapses + 1 : lapses);
  }

  function preview(state, now = nowIso()) {
    const out = {};
    for (const grade of Object.keys(RATINGS)) {
      const next = review(state, grade, now);
      out[grade] = { intervalDays: next.intervalDays, due: next.due };
    }
    return Object.freeze(out);
  }

  return Object.freeze({ initState, review, preview, weights: w });
}

function freezeState(stability, difficulty, intervalDays, due, lastReviewed, repetitions, lapses) {
  const s = round4(Math.max(MIN_STABILITY, stability));
  const d = round4(clampD(difficulty));
  return Object.freeze({
    stability: s,
    difficulty: d,
    intervalDays,
    due,
    lastReviewed,
    repetitions,
    lapses,
    ease: round2(easeFromDifficulty(d)), // DERIVED compat field — not scheduler state (see header)
  });
}

// The default scheduler — the stock FSRS-4.5 weights. The named exports below are the SAME
// functions every pre-OP·2 caller used; behavior is byte-identical when no per-app params exist.
const DEFAULT_SCHEDULER = createScheduler(W);
export const DEFAULT_WEIGHTS = W;

/**
 * A card's first review: initialize FSRS state straight from the rating.
 * @param {"again"|"hard"|"good"|"easy"} grade
 * @param {string} [now] ISO-8601 timestamp
 */
export const initState = DEFAULT_SCHEDULER.initState;

/**
 * Apply one review and return the next FSRS state (default weights).
 *  - `state == null`  → first review, initializes from the rating (see initState).
 *  - a legacy SM-2 state (has `ease`, no `stability`) → lazily converted before the FSRS update:
 *    stability = max(0.1, its intervalDays); difficulty = difficultyFromEase(its ease). No due-date
 *    shock — the card keeps its history and moves forward on FSRS from here.
 * @param {object|null} state
 * @param {"again"|"hard"|"good"|"easy"} grade
 * @param {string} [now] ISO-8601 timestamp of the review
 */
export const review = DEFAULT_SCHEDULER.review;

/**
 * Is the card due at `now`?
 * @param {{due:string}} state
 * @param {string} [now] ISO-8601 timestamp
 */
export function isDue(state, now = nowIso()) {
  return Date.parse(state.due) <= Date.parse(now);
}

/**
 * Non-mutating preview of every grade — what each button would schedule (default weights).
 * `preview(null, now)` yields the four first-rating predictions.
 * @returns {{again:object,hard:object,good:object,easy:object}} each = { intervalDays, due }
 */
export const preview = DEFAULT_SCHEDULER.preview;
